import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { setIdToken, setRefreshToken, setSessionToken } from "../api/authToken";
import { parseBffCallbackTokens } from "../api/services/iamService";
import { useAuth } from "../contexts/AuthContext";

const AuthCallbackPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithSessionToken } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function handleCallback() {
      try {
        const tokens = parseBffCallbackTokens(location.search);
        if (tokens.accessToken) {
          setSessionToken(tokens.accessToken);
        }
        if (!tokens.idToken) {
          throw new Error("Missing ID token from IAM callback");
        }
        setIdToken(tokens.idToken);
        if (tokens.refreshToken) {
          setRefreshToken(tokens.refreshToken);
        }

        await loginWithSessionToken();
        if (!cancelled) {
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Sign-in failed");
        }
      }
    }

    void handleCallback();
    return () => {
      cancelled = true;
    };
  }, [location.search, loginWithSessionToken, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 2,
        }}
      >
        <Typography color="error">{error}</Typography>
        <Typography
          component="a"
          href="#/"
          sx={{ color: "primary.main", textDecoration: "none" }}
        >
          Return home
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography color="text.secondary">Completing sign-in…</Typography>
    </Box>
  );
};

export default AuthCallbackPage;
